import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Building2, Users, Sparkles, GitBranch, ShieldCheck, UserX, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import OrgTree from "@/pages/OrganisationTree";

// ---------- helpers ---------- //
const KIND_STYLE = {
  executive_office: { label: "EXEC OFFICE", color: "#D4AF37" },
  division: { label: "DIVISION", color: "#7DD3FC" },
  department: { label: "DEPARTMENT", color: "#C0C7DA" },
  corporate_support: { label: "CORPORATE SUPPORT", color: "#F0A6A6" },
  ring_fence: { label: "RING-FENCED", color: "#EAB308" },
};

const LEVEL_ORDER = { exec: 0, head: 1, md: 2, director: 3, manager: 4, lead: 5, senior: 6, specialist: 7, analyst: 8, coordinator: 9 };

const eyebrowClass = "text-[10px] uppercase tracking-[0.15em] text-slate-500 font-mono";

// ---------- page ---------- //
export default function Organisation() {
  const { user } = useAuth();
  const [tab, setTab] = useState("chart");
  const [structure, setStructure] = useState(null);
  const [myWork, setMyWork] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const isOrgAdmin = user?.role === "admin" || user?.is_org_admin;
  const isPandoraInternal = user?.org_id === "pandora_internal";

  const load = async () => {
    setLoading(true);
    try {
      const [s, m, r] = await Promise.all([
        api.get("/org/structure"),
        api.get("/org/my-work"),
        api.get("/org/approval-routes").catch(() => ({ data: [] })),
      ]);
      setStructure(s.data);
      setMyWork(m.data);
      setRoutes(r.data);
    } catch (e) {
      toast.error("Failed to load organisation structure");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runSeed = async () => {
    if (!isOrgAdmin || !isPandoraInternal) return;
    if (!window.confirm("Seed the Pandora Management Consult org structure into this workspace? This creates VACANT positions only — no users, invitations, or access grants.")) return;
    setSeeding(true);
    try {
      const r = await api.post("/org/seed-pandora-structure");
      toast.success(`Seeded ${r.data.created.departments} departments · ${r.data.created.positions} positions · ${r.data.created.approval_route_drafts} draft routes`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return (
    <div className="p-10 flex items-center justify-center text-slate-500 font-mono text-xs">
      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> LOADING ORGANISATION…
    </div>
  );

  const totals = structure?.totals || {};
  const isEmpty = !structure || (structure.departments || []).length === 0;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="organisation-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
            <span className={eyebrowClass}>// CONFIGURABLE ORG STRUCTURE</span>
          </div>
          <h1 className="font-display text-3xl text-white tracking-tight">Organisation</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Departments, positions, reporting lines and approval routes — per-tenant. Reporting is metadata only; it does not grant record access.
          </p>
        </div>
        {isOrgAdmin && isEmpty && isPandoraInternal && (
          <button
            onClick={runSeed}
            disabled={seeding}
            data-testid="seed-btn"
            className="px-4 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Seed Pandora structure
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat eyebrow="DEPARTMENTS" value={totals.departments || 0} label="departments & divisions" />
        <Stat eyebrow="POSITIONS" value={totals.positions || 0} label="job slots" />
        <Stat eyebrow="ASSIGNED" value={totals.assigned_positions || 0} label="filled" color="#22C55E" />
        <Stat eyebrow="VACANT" value={totals.vacant_positions || 0} label="open" color={totals.vacant_positions > 0 ? "#F59E0B" : "#94A3B8"} />
        <Stat eyebrow="UNIQUE STAFF" value={totals.unique_staff || 0} label="people assigned" />
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div className="nexus-card p-10 text-center">
          <Building2 className="w-10 h-10 mx-auto text-slate-600 mb-4" strokeWidth={1} />
          <div className="font-display text-xl text-white">No organisation structure yet</div>
          <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
            {isPandoraInternal
              ? "You can seed the pre-configured Pandora Management Consult structure to get started, then invite people to positions."
              : "Ask your organisation administrator to configure departments and positions."}
          </p>
        </div>
      )}

      {/* Tabs */}
      {!isEmpty && (
        <>
          <div className="border-b border-white/10 flex gap-2" data-testid="org-tabs">
            {[
              { id: "chart", label: "Org Chart", icon: GitBranch },
              { id: "directory", label: "Directory", icon: Users },
              { id: "my-work", label: "My Work", icon: Building2 },
              { id: "routes", label: "Approval Routes", icon: ShieldCheck },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`org-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2.5 flex items-center gap-2 text-sm border-b-2 transition ${active ? "border-[#D4AF37] text-white" : "border-transparent text-slate-400 hover:text-white"}`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === "chart" && <ChartTab structure={structure} />}
          {tab === "directory" && <DirectoryTab structure={structure} />}
          {tab === "my-work" && <MyWorkTab data={myWork} />}
          {tab === "routes" && <RoutesTab routes={routes} onRefresh={load} isOrgAdmin={isOrgAdmin} />}
        </>
      )}
    </div>
  );
}

// ---------- Chart Tab (tree) ---------- //
function ChartTab({ structure }) {
  const { departments = [], positions = [] } = structure || {};
  const sortedPositions = useMemo(() => {
    return [...positions].sort((a, b) => (LEVEL_ORDER[a.level] ?? 99) - (LEVEL_ORDER[b.level] ?? 99));
  }, [positions]);
  return <OrgTree departments={departments} positions={sortedPositions} />;
}


// ---------- Directory Tab ---------- //
function DirectoryTab({ structure }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | vacant | filled
  const { positions = [], departments = [] } = structure || {};
  const deptMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);

  const filtered = positions.filter((p) => {
    if (filter === "vacant" && !p.is_vacant) return false;
    if (filter === "filled" && p.is_vacant) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    if (p.title.toLowerCase().includes(ql)) return true;
    if ((deptMap[p.department_id]?.name || "").toLowerCase().includes(ql)) return true;
    if (p.holders?.some((h) => (h.name || "").toLowerCase().includes(ql) || (h.email || "").toLowerCase().includes(ql))) return true;
    return false;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap items-center">
        <input
          data-testid="directory-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search positions, departments or people…"
          className="flex-1 min-w-[240px] bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none"
        />
        <div className="flex border border-white/10">
          {[
            { id: "all", label: "All" },
            { id: "filled", label: "Filled" },
            { id: "vacant", label: "Vacant" },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-2 text-xs font-mono tracking-widest ${filter === f.id ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
            >
              {f.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="nexus-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0A111F]">
            <tr className="border-b border-white/10">
              {["Position", "Department", "Level", "Holder(s)"].map((h) => (
                <th key={h} className={`px-4 py-2.5 text-left ${eyebrowClass}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const dept = deptMap[p.department_id];
              return (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="text-sm text-white">{p.title}</div>
                    {p.notes && <div className="text-[11px] text-slate-500 mt-0.5">{p.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">{dept?.name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-[11px] uppercase text-slate-400">{p.level}</td>
                  <td className="px-4 py-3">
                    {p.is_vacant ? (
                      <span className="font-mono text-[10px] tracking-widest text-amber-400 flex items-center gap-1">
                        <UserX className="w-3 h-3" /> VACANT
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {p.holders.map((h) => (
                          <span key={h.id} className="text-xs bg-[#0A111F] border border-white/10 px-2 py-0.5">
                            {h.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">No positions match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- My Work Tab ---------- //
function MyWorkTab({ data }) {
  if (!data) return null;
  const positions = data.positions || [];
  const reports = data.direct_reports || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="nexus-card p-5" data-testid="my-positions">
        <div className={`${eyebrowClass} mb-3`}>// MY POSITIONS ({positions.length})</div>
        {positions.length === 0 ? (
          <div className="text-sm text-slate-500">You are not currently assigned to any position.</div>
        ) : (
          <div className="space-y-3">
            {positions.map((p) => (
              <div key={p.id} className="p-3 bg-[#0A111F]/60 border border-white/5">
                <div className="text-white text-sm font-medium">{p.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{p.department?.name} · {p.level?.toUpperCase()}</div>
                {p.responsibilities && <div className="text-xs text-slate-400 mt-2 leading-relaxed">{p.responsibilities}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="nexus-card p-5" data-testid="my-reports">
        <div className={`${eyebrowClass} mb-3`}>// DIRECT REPORTS ({reports.length})</div>
        {reports.length === 0 ? (
          <div className="text-sm text-slate-500">No direct reports.</div>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.id} className="p-2.5 bg-[#0A111F]/60 border border-white/5 flex items-center gap-3">
                <div className={`w-1.5 h-1.5 rounded-full`} style={{ background: r.is_vacant ? "#F59E0B" : "#22C55E" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{r.title}</div>
                  {r.is_vacant ? (
                    <div className="text-[11px] text-amber-400 font-mono">VACANT</div>
                  ) : (
                    <div className="text-[11px] text-slate-400 truncate">{r.holders.map((h) => h.name).join(", ")}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="nexus-card p-5 lg:col-span-2" data-testid="my-approvals">
        <div className={`${eyebrowClass} mb-3`}>// PENDING APPROVALS</div>
        <div className="flex items-start gap-3 p-3 border border-amber-500/20 bg-amber-500/5">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-300 leading-relaxed">
            {data.notice}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Routes Tab ---------- //
function RoutesTab({ routes, onRefresh, isOrgAdmin }) {
  const activate = async (r) => {
    if (!window.confirm(`Activate approval route "${r.name}"? Every step must have a fallback approver configured or the request will 400.`)) return;
    try {
      await api.post(`/org/approval-routes/${r.id}/activate`);
      toast.success(`Activated ${r.name}`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete route "${r.name}"?`)) return;
    try {
      await api.delete(`/org/approval-routes/${r.id}`);
      toast.success(`Deleted ${r.name}`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="p-3 border border-amber-500/20 bg-amber-500/5 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs text-slate-300 leading-relaxed">
          Approval routes exist here as <span className="text-white font-medium">drafts for configuration only</span>. The approval-execution engine is not wired yet — activating a route flags it as ready but no workflow will fire.
        </div>
      </div>

      {routes.length === 0 && (
        <div className="nexus-card p-10 text-center">
          <ShieldCheck className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
          <div className="text-sm text-slate-400">No approval routes yet.</div>
        </div>
      )}

      {routes.map((r) => (
        <div key={r.id} className="nexus-card p-5" data-testid={`route-${r.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-white text-base font-medium">{r.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border"
                  style={{
                    borderColor: r.status === "active" ? "#22C55E" : "#F59E0B",
                    color: r.status === "active" ? "#22C55E" : "#F59E0B",
                  }}>
                  {r.status}
                </span>
                <span className="font-mono text-[10px] tracking-widest text-slate-500">· {r.kind?.toUpperCase()}</span>
              </div>
              {r.description && <p className="text-xs text-slate-400 mt-1">{r.description}</p>}
            </div>
            {isOrgAdmin && r.status === "draft" && (
              <div className="flex gap-2">
                <button onClick={() => activate(r)} data-testid={`activate-${r.id}`} className="px-3 py-1.5 text-xs font-mono tracking-widest border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                  ACTIVATE
                </button>
                <button onClick={() => remove(r)} data-testid={`del-route-${r.id}`} className="px-3 py-1.5 text-xs font-mono tracking-widest border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400">
                  DELETE
                </button>
              </div>
            )}
          </div>

          <div className="mt-4 space-y-2">
            <div className={eyebrowClass}>// STEPS ({r.steps?.length || 0})</div>
            {(r.steps || []).map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 bg-[#0A111F]/60 border border-white/5">
                <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-mono flex items-center justify-center">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{s.label || s.approver_kind}</div>
                  <div className="text-[11px] text-slate-500">
                    approver: <span className="font-mono">{s.approver_kind}</span>
                    {s.named_role && <> · <span className="text-slate-300">{s.named_role}</span></>}
                    {s.allow_delegate && <> · delegates ok</>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {r.notes_for_review && (
            <div className="mt-3 p-3 bg-[#0A111F]/60 border border-white/5">
              <div className={`${eyebrowClass} mb-1`}>// NOTES FOR REVIEW</div>
              <div className="text-[12px] text-slate-300 whitespace-pre-line">{r.notes_for_review}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- shared ---------- //
const Stat = ({ eyebrow, value, label, color }) => (
  <div className="nexus-card p-3">
    <div className={eyebrowClass}>{eyebrow}</div>
    <div className="font-display text-2xl mt-1" style={{ color: color || "#F8FAFC" }}>{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);
